<template>
  <v-dialog dark :value="value" style="z-index: 100000" fullscreen transition="dialog-bottom-transition">
    <div class="converter-background">
      <div class="gradient-bg"></div>
      <v-toolbar dark color="transparent" flat class="pt-2">
        <v-btn icon dark @click="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <v-progress-linear :value="(step / 4) * 100" color="primary" height="6" class="mx-4 rounded-pill"></v-progress-linear>
      </v-toolbar>

      <v-window v-model="step" class="fill-height">
        <v-window-item :value="1" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Pläne upgraden</h1>
            <p class="step-subtitle">Wähle die alten Pläne, die du zu einem neuen, modernen Plan zusammenführen möchtest.</p>
            <v-list-item-group v-model="selectedPlans" multiple class="mb-4 content-scroll">
              <transition-group name="list-anim" tag="div">
                <v-list-item
                  v-for="plan in oldPlans.filter((plan) => plan.exercises.length)"
                  :key="plan._id"
                  :value="plan"
                  class="plan-item pa-3 mb-3">
                  <template v-slot:default="{ active, toggle }">
                    <v-list-item-content @click="toggle">
                      <v-list-item-title class="font-weight-bold white--text">{{ plan.form.title }}</v-list-item-title>
                      <v-list-item-subtitle class="mt-1">
                        {{ formatDate(plan.form.ts) }} • {{ plan.exercises ? plan.exercises.length : 0 }} Übungen
                      </v-list-item-subtitle>
                    </v-list-item-content>
                    <v-list-item-action>
                      <v-icon v-if="active" color="primary" size="32">mdi-check-circle</v-icon>
                      <v-icon v-else size="32">mdi-circle-outline</v-icon>
                    </v-list-item-action>
                  </template>
                </v-list-item>
              </transition-group>
            </v-list-item-group>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="processPlans" :disabled="selectedPlans.length === 0">Weiter</v-btn>
            </div>
          </div>
        </v-window-item>

        <v-window-item :value="2" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Doppelte Übungen</h1>
            <p class="step-subtitle">Einige Übungen sind in mehreren Plänen. Wähle die Version mit den Sätzen, die du behalten möchtest.</p>
            <div class="content-scroll">
              <transition-group name="list-anim" tag="div">
                <div v-for="(versions, exerciseId) in duplicateExercises" :key="versions[0].title" class="mb-5 duplicate-group">
                  <h3 class="mb-3 font-weight-bold">{{ versions[0].title }}</h3>
                  <v-radio-group v-model="resolvedDuplicates[exerciseId]">
                    <v-radio v-for="(exercise, index) in versions" :key="index + 'test'" :value="exercise" class="mb-2">
                      <template v-slot:label>
                        <v-card class="glass-card exercise-card-dest elevation-6 ml-2 pa-2" style="width: 100%">
                          <div class="d-flex align-center">
                            <v-img
                              :src="getImageForExercise(exercise)"
                              style="max-width: 50px"
                              class="rounded-lg"
                              height="50px"
                              width="50px"
                              cover
                              position="top center" />
                            <div class="exercise-content">
                              <div class="font-weight-bold white--text text-subtitle-1">Aus: {{ exercise.sourcePlanTitle }}</div>
                              <div v-if="exercise.sets && exercise.sets.length > 0" class="mt-1">
                                <span class="caption grey--text">{{ exercise.sets.length }} Sätze / {{ formatSetDetails(exercise.sets) }}</span>
                              </div>
                            </div>
                          </div>
                        </v-card>
                      </template>
                    </v-radio>
                  </v-radio-group>
                </div>
              </transition-group>
            </div>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="finalizeExerciseSelection">Bestätigen & Weiter</v-btn>
              <v-btn text @click="step = 1" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>

        <v-window-item :value="3" class="fill-height">
          <div class="step-wrapper">
            <h1 class="step-title">Split-Typ</h1>
            <p class="step-subtitle">Wähle die Struktur deines neuen Plans. Das bestimmt deine Trainingstage.</p>
            <div class="content-scroll">
              <v-item-group v-model="selectedSplitIndex" class="mb-6" mandatory>
                <transition-group name="list-anim" tag="div" class="row">
                  <v-col v-for="(split, index) in splitItems" class="pb-0" cols="12" :key="split.info">
                    <v-item v-slot="{ active, toggle }">
                      <v-card @click="toggle" class="split-item" :class="{ 'split-item--active': active }">
                        <div class="d-flex align-center pa-4">
                          <div class="flex-grow-1">
                            <div class="font-weight-bold text-h6">{{ split.text }}</div>
                            <p class="caption mb-0 text--lighten-2">{{ split.info }}</p>
                          </div>
                          <v-icon v-if="active" size="32" color="white">mdi-check-decagram</v-icon>
                        </div>
                      </v-card>
                    </v-item>
                  </v-col>
                </transition-group>
              </v-item-group>
            </div>
            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="selectSplitType" :disabled="selectedSplitIndex === null">Weiter</v-btn>
              <v-btn text @click="step = 2" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>

        <v-window-item :value="4" class="fill-height">
          <div class="step-wrapper" v-if="selectedSplitType">
            <h1 class="step-title">Übungen zuweisen</h1>
            <p class="step-subtitle">Füge die Übungen den richtigen Trainingstagen hinzu.</p>
            <div class="content-scroll">
              <h3 class="font-weight-bold mb-3 mt-2 white--text">Deine Splits</h3>
              <v-expansion-panels accordion v-model="panel" multiple>
                <v-expansion-panel v-for="(split, index) in selectedSplitType.workouts" :key="index + 'oioi'" class="split-panel mb-2">
                  <v-expansion-panel-header class="split-panel-header">
                    <span class="font-weight-bold">{{ split.title }}</span>
                    <div class="ml-auto chip-container">
                      <v-chip small color="primary" class="ml-2">{{ split.exercises.length }}</v-chip>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <div v-if="split.exercises.length === 0" class="text-center grey--text py-4">
                      Tippe unten auf eine Übung, um sie hier hinzuzufügen.
                    </div>
                    <transition-group name="list-anim" tag="div" class="mt-2">
                      <div
                        v-for="(exercise, exIndex) in split.exercises"
                        :key="exercise.instanceId"
                        class="assigned-exercise-item d-flex align-center">
                        <v-img :src="getImageForExercise(exercise)" class="rounded flex-shrink-0" height="40" width="40" cover />
                        <span class="white--text ml-3">{{ exercise.title }}</span>
                        <v-spacer></v-spacer>
                        <v-btn icon small @click="unassignExercise(exercise, index, exIndex)"><v-icon color="red">mdi-minus-circle</v-icon></v-btn>
                      </div>
                    </transition-group>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>

              <h3 class="font-weight-bold my-3 white--text">Verfügbare Übungen ({{ finalExercises.length }})</h3>
              <div v-if="finalExercises.length === 0" class="text-center grey--text py-4">Super! Alle Übungen zugewiesen.</div>
              <transition-group name="list-anim" tag="div">
                <div v-for="(exercise, index) in finalExercises" :key="exercise.instanceId" class="mb-2">
                  <v-card class="glass-card exercise-card-source elevation-4" @click="openAssignDialog(exercise, index)">
                    <div class="d-flex align-center pa-2">
                      <v-img
                        :src="getImageForExercise(exercise)"
                        style="max-width: 60px"
                        class="rounded-lg flex-shrink-0"
                        height="60px"
                        width="60px"
                        cover
                        position="top center" />
                      <div class="exercise-content">
                        <div class="font-weight-bold white--text text-subtitle-1">{{ exercise.title }}</div>
                        <span class="caption grey--text">{{ formatSetDetails(exercise.sets) }}</span>
                      </div>
                      <v-icon color="primary" size="32">mdi-plus-circle-outline</v-icon>
                    </div>
                  </v-card>
                </div>
              </transition-group>
            </div>

            <v-divider class="my-6"></v-divider>

            <div class="step-actions">
              <v-btn color="primary" x-large block rounded @click="prepareAndRedirect">Plan erstellen</v-btn>
              <v-btn text @click="step = 3" class="mt-2">Zurück</v-btn>
            </div>
          </div>
        </v-window-item>
      </v-window>
    </div>

    <v-bottom-sheet v-model="assignDialog" dark inset>
      <v-sheet dark class="text-center assign-sheet pa-4">
        <div v-if="exerciseToAssign" class="mb-4">
          <div class="text-h6 mb-1">"{{ exerciseToAssign.title }}"</div>
          <div class="caption grey--text text--darken-1">Welchen Splits möchtest du die Übung zuweisen?</div>
        </div>
        <v-list-item-group dark v-if="selectedSplitType" v-model="assignedSplits" multiple class="mb-4">
          <v-list-item
            dark
            v-for="(split, index) in selectedSplitType.workouts"
            :key="index + '-test2'"
            :value="index"
            class="plan-item mb-2"
            active-class="plan-item--active">
            <template v-slot:default="{ active, toggle }">
              <v-list-item-content @click="toggle">
                <v-list-item-title>{{ split.title }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon v-if="active" color="primary">mdi-check-circle</v-icon>
                <v-icon v-else>mdi-circle-outline</v-icon>
              </v-list-item-action>
            </template>
          </v-list-item>
        </v-list-item-group>
        <v-btn x-large block rounded color="primary" @click="assignExercise">Übernehmen</v-btn>
      </v-sheet>
    </v-bottom-sheet>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from "vuex"
import moment from "moment"
import draggable from "vuedraggable"

export default {
  name: "PlanConverter",
  components: { draggable },
  props: {
    value: { type: Boolean, required: true },
  },
  data() {
    return {
      step: 1,
      selectedPlans: [],
      duplicateExercises: {},
      resolvedDuplicates: {},
      finalExercises: [],
      selectedSplitType: null,
      selectedSplitIndex: null,
      assignDialog: false,
      exerciseToAssign: null,
      exerciseToAssignIndex: null,
      assignedSplits: [],
      panel: [],
      splitItems: [
        {
          text: "Ganzkörper-Training",
          value: "fullBodySplit",
          info: "Jede Trainingseinheit trainiert den gesamten Körper.",
          workouts: [{ title: "Ganzkörper", exercises: [] }],
        },
        {
          text: "Oberkörper-/Unterkörper Split",
          value: "upperLowerSplit",
          info: "Ein Tag für den Oberkörper, der andere für den Unterkörper.",
          workouts: [
            { title: "Oberkörper", exercises: [] },
            { title: "Unterkörper", exercises: [] },
          ],
        },
        {
          text: "Push/Pull/Beine-Split",
          value: "pushPullLegSplit",
          info: "Drückende, ziehende Muskeln und Beine an separaten Tagen.",
          workouts: [
            { title: "Push", exercises: [] },
            { title: "Pull", exercises: [] },
            { title: "Beine", exercises: [] },
          ],
        },
        {
          text: "Bro-Split",
          value: "broSplit",
          info: "Jede Muskelgruppe hat einen eigenen Trainingstag.",
          workouts: [
            { title: "Brust", exercises: [] },
            { title: "Rücken", exercises: [] },
            { title: "Schultern", exercises: [] },
            { title: "Beine", exercises: [] },
            { title: "Arme", exercises: [] },
          ],
        },
      ],
    }
  },
  computed: {
    ...mapGetters("runWorkoutStore", { oldPlans: "oldWorkoutPlans" }),
    ...mapGetters("deviceStore", ["exerciseList"]),
  },
  methods: {
    ...mapActions("runWorkoutStore", ["setPlanForConversion"]),
    formatDate(timestamp) {
      return moment.unix(Number(timestamp)).format("DD.MM.YYYY")
    },
    getImageForExercise(exercise) {
      // Diese Methode bleibt gleich
      const exId = exercise.exercise_id ? exercise.exercise_id._id || exercise.exercise_id : null
      const fullEx = this.exerciseList.find((e) => e._id === exId)
      if (fullEx && fullEx.picture) return `/img/uebungen/${fullEx.picture.replace(/\.[^/.]+$/, "")}.webp`
      if (fullEx && fullEx.manual && fullEx.manual[0]?.picture) return `/img/uebungen/${fullEx.manual[0].picture.replace(/\.[^/.]+$/, "")}.webp`
      return "/img/einmalig/imageUploadPlaceholder.jpg"
    },
    formatSetDetails(sets) {
      if (!sets || sets.length === 0) return "Keine Sätze"
      const firstSet = sets[0]
      const settings = Array.isArray(firstSet) ? firstSet : firstSet.settings
      if (!settings) return "Keine Satzdetails"

      const weight = settings.find((s) => s.text === "Gewicht")?.value
      const minReps = settings.find((s) => s.text === "Min Wiederholungen")?.value
      const maxReps = settings.find((s) => s.text === "Max Wiederholungen")?.value
      let details = []
      if (weight) details.push(`${weight}kg`)
      if (minReps && maxReps) details.push(`${minReps}-${maxReps} Wdh.`)
      else if (maxReps) details.push(`${maxReps} Wdh.`)
      return details.join(" / ")
    },
    processPlans() {
      const exerciseMap = new Map()
      this.selectedPlans.forEach((plan) => {
        if (plan.exercises) {
          plan.exercises.forEach((ex) => {
            const exerciseId = ex.exercise_id ? ex.exercise_id._id || ex.exercise_id : null
            if (!exerciseId) return
            const fullExercise = this.exerciseList.find((e) => e._id === exerciseId)
            if (!fullExercise) return

            // HIER WERDEN DIE ÜBUNGEN ANGEREICHERT
            const exerciseWithMeta = {
              ...ex,
              // Füge alle benötigten Felder aus dem globalen Objekt hinzu
              ...fullExercise,
              // Überschreibe den Titel, um sicherzustellen, dass er aktuell ist
              title: fullExercise.title,
              sourcePlanTitle: plan.form.title,
              instanceId: `${plan._id}-${exerciseId}`,
            }

            if (exerciseMap.has(exerciseId)) {
              exerciseMap.get(exerciseId).push(exerciseWithMeta)
            } else {
              exerciseMap.set(exerciseId, [exerciseWithMeta])
            }
          })
        }
      })
      const uniqueExercises = []
      const duplicates = {}
      exerciseMap.forEach((versions, id) => {
        if (versions.length > 1) {
          duplicates[id] = versions
          this.resolvedDuplicates[id] = versions[0]
        } else {
          uniqueExercises.push(versions[0])
        }
      })
      this.duplicateExercises = duplicates
      this.finalExercises = uniqueExercises
      if (Object.keys(duplicates).length > 0) {
        this.step = 2
      } else {
        this.step = 3
      }
    },
    finalizeExerciseSelection() {
      const resolved = Object.values(this.resolvedDuplicates)
      this.finalExercises = [...this.finalExercises, ...resolved]
      this.step = 3
    },
    selectSplitType() {
      const splitType = this.splitItems[this.selectedSplitIndex]
      this.selectedSplitType = JSON.parse(JSON.stringify(splitType))
      this.selectedSplitType.workouts.forEach((w) => this.$set(w, "exercises", []))
      this.panel = [...Array(this.selectedSplitType.workouts.length).keys()]
      this.step = 4
    },
    openAssignDialog(exercise, index) {
      this.exerciseToAssign = exercise
      this.exerciseToAssignIndex = index
      this.assignedSplits = this.selectedSplitType.workouts
        .map((split, splitIndex) => {
          return split.exercises.some((ex) => ex.instanceId === exercise.instanceId) ? splitIndex : null
        })
        .filter((i) => i !== null)
      this.assignDialog = true
    },
    assignExercise() {
      this.selectedSplitType.workouts.forEach((split) => {
        const index = split.exercises.findIndex((ex) => ex.instanceId === this.exerciseToAssign.instanceId)
        if (index > -1) split.exercises.splice(index, 1)
      })
      this.assignedSplits.forEach((splitIndex) => {
        this.selectedSplitType.workouts[splitIndex].exercises.push(this.exerciseToAssign)
      })
      if (this.assignedSplits.length > 0) {
        const availableIndex = this.finalExercises.findIndex((ex) => ex.instanceId === this.exerciseToAssign.instanceId)
        if (availableIndex > -1) this.finalExercises.splice(availableIndex, 1)
      }
      this.assignDialog = false
    },
    unassignExercise(exercise, splitIndex, exerciseIndex) {
      this.selectedSplitType.workouts[splitIndex].exercises.splice(exerciseIndex, 1)
      const isAssignedElsewhere = this.selectedSplitType.workouts.some((split) => split.exercises.some((ex) => ex.instanceId === exercise.instanceId))
      if (!isAssignedElsewhere) {
        this.finalExercises.push(exercise)
      }
    },
    prepareAndRedirect() {
      if (this.finalExercises.length > 0) {
        alert("Bitte weise alle verfügbaren Übungen einem Split zu, bevor du den Plan erstellst.")
        return
      }

      this.selectedSplitType.workouts.forEach((split) => {
        split.exercises.forEach((exercise) => {
          // 1. Datenstruktur für Sätze konvertieren
          if (exercise.sets && exercise.sets.length > 0 && exercise.sets[0].settings) {
            exercise.sets = exercise.sets.map((set) => set.settings)
          }

          // 2. Zusammenfassungs-Werte berechnen (weightSum & repSum)
          if (exercise.sets && exercise.sets.length > 0) {
            const weights = exercise.sets.map((set) => parseFloat(set.find((item) => item.text === "Gewicht")?.value || 0)).filter((v) => !isNaN(v))
            const minReps = exercise.sets
              .map((set) => parseInt(set.find((item) => item.text === "Min Wiederholungen")?.value || 0))
              .filter((v) => !isNaN(v))
            const maxReps = exercise.sets
              .map((set) => parseInt(set.find((item) => item.text === "Max Wiederholungen")?.value || 0))
              .filter((v) => !isNaN(v))

            if (weights.length > 0) {
              exercise.weightSum = { min: Math.min(...weights), max: Math.max(...weights) }
            }
            if (minReps.length > 0 || maxReps.length > 0) {
              const allReps = [...minReps, ...maxReps]
              exercise.repSum = { min: Math.min(...allReps), max: Math.max(...allReps) }
            }
          }
        })
      })

      const newPlanForm = {
        title: `Konvertierter Plan - ${moment().format("DD.MM.YY")}`,
        ts: moment().unix().toString(),
        duration: 60,
        splits: this.selectedSplitType,
      }

      this.setPlanForConversion(newPlanForm)
      this.$router.push({ name: "WorkoutCreateNew", params: { conversion: "true" } })
    },
  },
}
</script>

<style scoped>
/* GENERAL & ANIMATIONS */
.converter-background {
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  height: 100vh;
  width: 100vw;
  display: flex;
  flex-direction: column;
}
.step-wrapper {
  padding: 0 16px 100px 16px;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.step-title {
  color: var(--v-primary);
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 8px;
}
.step-subtitle {
  font-size: 1.1rem;
  color: #e8e7e7;
  margin-bottom: 24px;
}
.content-scroll {
  flex-grow: 1;
  overflow-y: auto;
  padding-right: 4px;
}
.step-actions {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  background: linear-gradient(to top, rgba(26, 26, 26, 1) 50%, rgba(26, 26, 26, 0));
}

.list-anim-enter-active,
.list-anim-leave-active {
  transition: all 0.5s cubic-bezier(0.55, 0, 0.1, 1);
}
.list-anim-enter,
.list-anim-leave-to {
  opacity: 0;
  transform: scale(0.95) translateY(20px);
}

.plan-item,
.split-item,
.glass-card {
  border-radius: 16px !important;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.2s ease-in-out;
  cursor: pointer;
}
.plan-item.v-list-item--active,
.split-item.split-item--active {
  background-color: rgba(0, 150, 63, 0.2);
  border-color: var(--v-primary-base);
  box-shadow: 0 0 20px rgba(0, 150, 63, 0.5);
}
.plan-item-light {
  border-radius: 16px !important;
  background-color: #f0f0f0;
  border: 1px solid #e0e0e0;
}
.plan-item--active {
  background-color: rgba(0, 150, 63, 0.2) !important;
  border-color: rgba(0, 150, 63, 0.2) !important;
}

.split-panel {
  background-color: rgba(255, 255, 255, 0.05) !important;
  border-radius: 16px !important;
}
.split-panel-header {
  font-size: 1.1rem;
}
.chip-container {
  flex-grow: 0;
  flex-shrink: 0;
}
.assigned-exercise-item {
  background-color: rgba(0, 0, 0, 0.2);
  padding: 8px 12px;
  border-radius: 12px;
  margin-bottom: 8px;
}
.exercise-card-source {
  cursor: pointer;
  transition: transform 0.2s ease;
}
.exercise-card-source:active {
  transform: scale(0.95);
}
.exercise-content {
  flex-grow: 1;
  padding: 0 16px;
  min-width: 0;
}

.assign-sheet {
  border-top-left-radius: 20px !important;
  border-top-right-radius: 20px !important;
}
.gradient-bg {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: radial-gradient(circle at 15% 25%, rgba(0, 150, 63, 0.1), transparent 45%),
    radial-gradient(circle at 85% 75%, rgba(108, 99, 255, 0.1), transparent 45%);
  pointer-events: none;
}
</style>
